import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Redirect, useHistory, useParams } from "react-router-dom";
import {
  FaBookOpen,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaClipboardList,
  FaGraduationCap,
  FaHashtag,
  FaSave,
  FaSyncAlt,
  FaUser,
  FaUsers,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import boyImage from "../img/boy.jpg";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const TAG_ALT = "ALTERNATIVE";

//Created By Cursor — absent mark codes accepted in mark entry
const ABSENT_MARK = "AB";

//Created By Cursor
function isAbsentMarkCode(value) {
  return String(value || "").trim().toUpperCase() === ABSENT_MARK;
}

//Created By Cursor
function isPartialAbsentMarkCode(value) {
  const v = String(value || "").trim().toUpperCase();
  return v === "A" || v === "AB";
}

//Created By Cursor
function markInputBlankOrSpaceError(value) {
  const s = String(value ?? "");
  if (s.trim() === "") {
    return "Mark cannot be blank";
  }
  if (/\s/.test(s)) {
    return "Spaces are not allowed";
  }
  return "";
}

//Created By Cursor
function handleMarkKeyDown(e) {
  const root = document.querySelector(
    ".payroll-page.eamstw-page .eamstw-content"
  );
  if (!root) return;

  const inputs = Array.from(
    root.querySelectorAll('input.eamstw-input[type="text"]:not(:disabled)')
  );
  const currentIndex = inputs.indexOf(e.target);
  if (currentIndex < 0) return;

  const move = (delta) => {
    const next = inputs[currentIndex + delta];
    if (next) {
      e.preventDefault();
      next.focus();
      next.select();
      return true;
    }
    return false;
  };

  const focusSave = () => {
    const saveBtn = root.querySelector(".eamstw-save-btn");
    if (saveBtn) {
      e.preventDefault();
      saveBtn.focus();
    }
  };

  if (e.key === "Enter") {
    if (!move(1)) focusSave();
    return;
  }

  if (e.key === "Tab" && !e.shiftKey) {
    if (!move(1)) focusSave();
    return;
  }

  if (e.key === "Tab" && e.shiftKey) {
    move(-1);
    return;
  }

  if (e.key === "ArrowRight" || e.key === "ArrowDown") move(1);
  if (e.key === "ArrowLeft" || e.key === "ArrowUp") move(-1);
}

//Created By Cursor
function classLabel(item) {
  if (!item) return "Class";
  if (item.section_id === "No" || !item.section_name) {
    return item.class_name || "Class";
  }
  return `${item.class_name}: ${item.section_name}`;
}

//Created By Cursor
function studentPhotoSrc(student) {
  return student?.student_img_path || student?.stu_photo || boyImage;
}

//Created By Cursor
function studentInitial(name) {
  return name?.charAt(0)?.toUpperCase() || "S";
}

const ExamAddMarkStudentWise = () => {
  const history = useHistory();
  const {
    exam_id: EXAM_ID,
    session_id: SESSION_ID,
    exam_name,
    exam_status,
  } = useParams();

  const EXAM_NAME = decodeURIComponent(exam_name || "");
  const EXAM_STATUS = decodeURIComponent(exam_status || "");
  const loggedIn = localStorage.getItem("token") !== null;

  const [bootLoading, setBootLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingMarks, setLoadingMarks] = useState(false);
  const [classList, setClassList] = useState([]);
  const [selectedClassPosition, setSelectedClassPosition] = useState(-1);
  const [studentList, setStudentList] = useState([]);
  const [selectedStudentIndex, setSelectedStudentIndex] = useState(-1);
  const [subjectList, setSubjectList] = useState([]);
  const [studentMarks, setStudentMarks] = useState({});
  const [classSubjectPaper, setClassSubjectPaper] = useState([]);
  const [savingStudentId, setSavingStudentId] = useState(null);
  const [studentAltSub, setStudentAltSub] = useState([]);
  const [avatarFallback, setAvatarFallback] = useState({});

  const selectedClass = useMemo(() => {
    return selectedClassPosition >= 0 && classList[selectedClassPosition]
      ? classList[selectedClassPosition]
      : null;
  }, [selectedClassPosition, classList]);

  const classSectionData = useMemo(() => {
    if (!selectedClass) return { classId: null, sectionId: null };
    return {
      classId: selectedClass.class_id,
      sectionId:
        selectedClass.section_id === "null" ? "No" : selectedClass.section_id,
    };
  }, [selectedClass]);

  const selectedStudent =
    selectedStudentIndex >= 0 ? studentList[selectedStudentIndex] : null;

  //Created By Cursor
  const getExamParticipatingClass = useCallback(
    async ({ soft = false } = {}) => {
      if (!EXAM_ID || !SESSION_ID) {
        setClassList([]);
        setBootLoading(false);
        setRefreshing(false);
        return;
      }

      if (soft) setRefreshing(true);
      else setBootLoading(true);

      try {
        const response = await axios.get(
          `${API_BASE()}getClassWithSubjectsPartAndPaper.php`,
          {
            params: {
              school_code: SessionManager.getSchoolCode(),
              exam_id: EXAM_ID,
              session_id: SESSION_ID,
            },
          }
        );

        if (response.data?.api_status === "OK") {
          setClassList(response.data.data || []);
          setSelectedClassPosition(-1);
          setStudentList([]);
          setSelectedStudentIndex(-1);
          setSubjectList([]);
          setClassSubjectPaper([]);
        } else {
          setClassList([]);
          myToast.notify_warning(response.data?.api_remark);
        }
      } catch (error) {
        console.error("Error fetching class data:", error);
        myToast.notify_error("Error fetching class data");
        setClassList([]);
      } finally {
        setBootLoading(false);
        setRefreshing(false);
      }
    },
    [EXAM_ID, SESSION_ID]
  );

  const getStudentListAndMarks = useCallback(
    (classId, sectionId) => {
      if (!classId || !sectionId || !selectedClass) return;

      const subjects = selectedClass.subject || [];
      if (subjects.length === 0) {
        setStudentList([]);
        return;
      }

      setLoadingStudents(true);
      setLoadingMarks(true);

      const markPromises = subjects.map((subject) => {
        if (!subject.subject_id) {
          return Promise.resolve({
            data: { api_status: "ERROR", api_remark: "Missing subject_id" },
          });
        }

        return axios
          .get(`${API_BASE()}getClassSubjectMarks.php`, {
            params: {
              school_code: SessionManager.getSchoolCode(),
              exam_id: EXAM_ID,
              class_id: classId,
              section_id: sectionId,
              subject_id: subject.subject_id,
              session_id: SESSION_ID,
            },
          })
          .catch((error) => {
            console.error(
              `Error fetching marks for subject ${subject.subject_id}:`,
              error
            );
            return { data: { api_status: "ERROR", api_remark: error.message } };
          });
      });

      Promise.all(markPromises)
        .then((responses) => {
          setLoadingStudents(false);
          setLoadingMarks(false);

          const firstResponse = responses.find(
            (r) => r.data && r.data.api_status === "OK"
          );
          if (firstResponse && firstResponse.data.api_status === "OK") {
            const apiData = firstResponse.data.data || firstResponse.data;
            setStudentList(apiData.student_list || []);
            setSelectedStudentIndex(-1);
          } else {
            myToast.notify_warning("Error fetching student list");
            setStudentList([]);
          }

          const allStudentAltSub = [];
          responses.forEach((response) => {
            if (response.data && response.data.api_status === "OK") {
              const apiData = response.data.data || response.data;
              if (Array.isArray(apiData.student_alt_sub)) {
                allStudentAltSub.push(...apiData.student_alt_sub);
              }
            }
          });
          setStudentAltSub(allStudentAltSub);

          const allStudentMarks = {};

          responses.forEach((response, index) => {
            if (response.data && response.data.api_status === "OK") {
              const subject = subjects[index];
              const subjectId = subject.subject_id;
              const apiData = response.data.data || response.data;
              const markStatus = apiData.mark_status || [];

              markStatus.forEach((mark) => {
                const studentId = (
                  mark.student_id ||
                  mark.student_enrollment_no ||
                  ""
                ).toLowerCase();
                if (!studentId) return;

                if (!allStudentMarks[studentId]) {
                  allStudentMarks[studentId] = {};
                }

                if (!allStudentMarks[studentId][subjectId]) {
                  allStudentMarks[studentId][subjectId] = [];
                }

                allStudentMarks[studentId][subjectId].push({
                  ...mark,
                  obtained_mark_tmp:
                    mark.obtained_mark_tmp !== undefined &&
                    mark.obtained_mark_tmp !== null
                      ? mark.obtained_mark_tmp
                      : mark.obtained_mark || "",
                  grade_obtained_tmp:
                    mark.grade_obtained_tmp !== undefined &&
                    mark.grade_obtained_tmp !== null
                      ? mark.grade_obtained_tmp
                      : mark.grade_obtained || "",
                  obtained_mark_new: mark.obtained_mark_new || "",
                  grade_obtained_new: mark.grade_obtained_new || "",
                  changed_mark: mark.changed_mark || "0",
                });
              });
            }
          });

          const normalizedAllStudentMarks = {};
          Object.keys(allStudentMarks).forEach((studentId) => {
            normalizedAllStudentMarks[studentId] = {};
            Object.keys(allStudentMarks[studentId]).forEach((subjectId) => {
              normalizedAllStudentMarks[studentId][subjectId.toLowerCase()] =
                allStudentMarks[studentId][subjectId];
            });
          });

          setStudentMarks((prev) => {
            const updated = { ...prev };
            Object.keys(normalizedAllStudentMarks).forEach((studentId) => {
              if (!updated[studentId]) {
                updated[studentId] = {};
              }
              Object.keys(normalizedAllStudentMarks[studentId]).forEach(
                (subjectId) => {
                  if (!updated[studentId][subjectId]) {
                    updated[studentId][subjectId] =
                      normalizedAllStudentMarks[studentId][subjectId];
                  } else {
                    const existingMarks = updated[studentId][subjectId];
                    const serverMarks =
                      normalizedAllStudentMarks[studentId][subjectId];

                    const existingMap = new Map();
                    existingMarks.forEach((m) => {
                      const paperId = m.exam_paper_id?.toLowerCase();
                      if (paperId) {
                        existingMap.set(paperId, m);
                      }
                    });

                    updated[studentId][subjectId] = serverMarks.map(
                      (serverMark) => {
                        const paperId = serverMark.exam_paper_id?.toLowerCase();
                        const existingMark = existingMap.get(paperId);

                        if (existingMark && existingMark.changed_mark === "1") {
                          return existingMark;
                        }

                        return serverMark;
                      }
                    );
                  }
                }
              );
            });
            return updated;
          });
        })
        .catch((error) => {
          setLoadingStudents(false);
          setLoadingMarks(false);
          myToast.notify_error("Error fetching student data");
          console.error("Error fetching student data:", error);
          setStudentList([]);
        });
    },
    [SESSION_ID, EXAM_ID, selectedClass]
  );

  useEffect(() => {
    if (selectedClass) {
      const { classId, sectionId } = classSectionData;
      getStudentListAndMarks(classId, sectionId);
      setClassSubjectPaper(selectedClass.subject_paper || []);
      setSubjectList(selectedClass.subject || []);
    }
  }, [selectedClass, classSectionData, getStudentListAndMarks]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getExamParticipatingClass();
  }, [getExamParticipatingClass]);

  //Created By Cursor
  const getStudentSubjectMarks = useCallback(
    (studentId, subjectId) => {
      if (!studentId || !subjectId) return [];
      const studentIdLower = studentId.toLowerCase();
      const subjectIdLower = subjectId.toLowerCase();
      const studentMarksData = studentMarks[studentIdLower];

      if (!studentMarksData) {
        return [];
      }

      const subjectKey = Object.keys(studentMarksData).find(
        (key) => key.toLowerCase() === subjectIdLower
      );

      if (!subjectKey) {
        return [];
      }

      return studentMarksData[subjectKey] || [];
    },
    [studentMarks]
  );

  //Created By Cursor
  const getSubjectPapers = useCallback(
    (subjectId) => {
      if (!subjectId || !classSubjectPaper.length) return [];
      const subjectIdLower = subjectId.toLowerCase();
      return classSubjectPaper.filter(
        (paper) => paper.subject_id?.toLowerCase() === subjectIdLower
      );
    },
    [classSubjectPaper]
  );

  const isSubjectGrantedToStudent = useCallback(
    (studentId, subjectId) => {
      if (!studentId || !subjectId || !studentAltSub.length) return true;

      const studentIdLower = studentId.toLowerCase();
      const subjectIdLower = subjectId.toLowerCase();

      const isAlternativeSubject = subjectList.some(
        (sub) =>
          sub.subject_id?.toLowerCase() === subjectIdLower &&
          sub.subject_type?.toLowerCase() === TAG_ALT.toLowerCase()
      );

      if (!isAlternativeSubject) {
        return true;
      }

      return studentAltSub.some(
        (altSub) =>
          altSub.subject_id?.toLowerCase() === subjectIdLower &&
          (altSub.student_id?.toLowerCase() === studentIdLower ||
            altSub.student_enrollment_no?.toLowerCase() === studentIdLower)
      );
    },
    [studentAltSub, subjectList]
  );

  const getFilteredSubjectList = useCallback(
    (studentId) => {
      if (!studentId || !subjectList.length) return [];

      return subjectList.filter((subject) =>
        isSubjectGrantedToStudent(studentId, subject.subject_id)
      );
    },
    [subjectList, isSubjectGrantedToStudent]
  );

  const hasStudentMarksEntered = useCallback(
    (studentId) => {
      if (!studentId || !subjectList.length) return false;
      const studentIdLower = studentId.toLowerCase();
      const studentMarksData = studentMarks[studentIdLower];

      if (!studentMarksData) return false;

      const filteredSubjects = getFilteredSubjectList(studentId);

      return filteredSubjects.some((subject) => {
        const subjectIdLower = subject.subject_id?.toLowerCase();
        const subjectKey = Object.keys(studentMarksData).find(
          (key) => key.toLowerCase() === subjectIdLower
        );

        if (!subjectKey || !studentMarksData[subjectKey]) return false;

        const marks = studentMarksData[subjectKey];
        return marks.some(
          (mark) =>
            (mark.obtained_mark && mark.obtained_mark !== "") ||
            (mark.grade_obtained && mark.grade_obtained !== "") ||
            (mark.obtained_mark_tmp && mark.obtained_mark_tmp !== "") ||
            (mark.grade_obtained_tmp && mark.grade_obtained_tmp !== "") ||
            (mark.obtained_mark_new && mark.obtained_mark_new !== "") ||
            (mark.grade_obtained_new && mark.grade_obtained_new !== "")
        );
      });
    },
    [studentMarks, subjectList, getFilteredSubjectList]
  );

  const getStudentMarksProgress = useCallback(
    (studentId) => {
      if (!studentId || !subjectList.length || !classSubjectPaper.length) {
        return { saved: 0, total: 0, percentage: 0 };
      }

      const filteredSubjects = getFilteredSubjectList(studentId);
      if (filteredSubjects.length === 0) {
        return { saved: 0, total: 0, percentage: 0 };
      }

      let totalPapers = 0;
      let savedMarks = 0;

      filteredSubjects.forEach((subject) => {
        const papers = getSubjectPapers(subject.subject_id);
        totalPapers += papers.length;

        const marks = getStudentSubjectMarks(studentId, subject.subject_id);
        marks.forEach((mark) => {
          if (mark.obtained_mark || mark.grade_obtained) {
            savedMarks++;
          }
        });
      });

      return {
        saved: savedMarks,
        total: totalPapers,
        percentage:
          totalPapers > 0 ? Math.round((savedMarks / totalPapers) * 100) : 0,
      };
    },
    [
      subjectList,
      classSubjectPaper,
      getSubjectPapers,
      getStudentSubjectMarks,
      getFilteredSubjectList,
    ]
  );

  //Created By Cursor
  const handleMarkChange = (
    studentId,
    subjectId,
    paperId,
    value,
    isMarkSystem,
    paperMeta = {}
  ) => {
    const studentIdLower = studentId?.toLowerCase();
    const subjectIdLower = subjectId?.toLowerCase();
    const paperIdLower = paperId?.toLowerCase();
    const raw = value !== null && value !== undefined ? String(value) : "";

    setStudentMarks((prev) => {
      const updated = { ...prev };
      if (!updated[studentIdLower]) {
        updated[studentIdLower] = {};
      }
      if (!updated[studentIdLower][subjectIdLower]) {
        updated[studentIdLower][subjectIdLower] = [];
      }

      const list = updated[studentIdLower][subjectIdLower];
      const paperIndex = list.findIndex(
        (p) => p.exam_paper_id?.toLowerCase() === paperIdLower
      );

      const prevPaper =
        paperIndex >= 0
          ? list[paperIndex]
          : {
              exam_paper_id: paperId,
              subject_id: subjectId,
              student_id: studentId,
              max_mark: paperMeta.max_mark || "",
              max_grade: paperMeta.max_grade || "",
              is_mark_system:
                paperMeta.is_mark_system || (isMarkSystem ? "1" : "0"),
              is_grade_system:
                paperMeta.is_grade_system || (isMarkSystem ? "0" : "1"),
              obtained_mark: "",
              grade_obtained: "",
            };

      const nextPaper = { ...prevPaper };

      if (!isMarkSystem) {
        nextPaper.grade_obtained_tmp = raw;
        nextPaper.grade_obtained_new = raw;
        nextPaper.changed_mark = "1";
        nextPaper.set_error = "0";
        delete nextPaper.errorMessage;
      } else {
        const max_mark = parseFloat(
          paperMeta.max_mark != null && paperMeta.max_mark !== ""
            ? paperMeta.max_mark
            : prevPaper.max_mark
        );
        const maxMarkNum = Number.isNaN(max_mark) ? Infinity : max_mark;

        const blankOrSpaceError = markInputBlankOrSpaceError(raw);
        if (blankOrSpaceError) {
          nextPaper.obtained_mark_tmp = raw;
          nextPaper.set_error = "1";
          nextPaper.errorMessage = blankOrSpaceError;
          nextPaper.changed_mark = "1";
        } else if (isAbsentMarkCode(raw)) {
          //Created By Cursor — store absent as AB (accepts AB / Ab / ab)
          nextPaper.obtained_mark_tmp = ABSENT_MARK;
          nextPaper.obtained_mark_new = ABSENT_MARK;
          nextPaper.changed_mark = "1";
          nextPaper.set_error = "0";
          delete nextPaper.errorMessage;
        } else if (isPartialAbsentMarkCode(raw)) {
          nextPaper.obtained_mark_tmp = raw;
          nextPaper.set_error = "0";
          delete nextPaper.errorMessage;
        } else {
          const numericPattern = /^-?\d*\.?\d*$/;
          if (!numericPattern.test(raw)) {
            nextPaper.obtained_mark_tmp = raw;
            nextPaper.set_error = "1";
            nextPaper.errorMessage =
              "Enter a number or AB (no blank or spaces)";
          } else {
            const inputMark = parseFloat(raw);
            if (Number.isNaN(inputMark) || raw.trim() === "") {
              nextPaper.obtained_mark_tmp = raw;
              nextPaper.set_error = "1";
              nextPaper.errorMessage =
                "Enter a number or AB (no blank or spaces)";
            } else if (inputMark < 0) {
              myToast.notify_warning("Marks cannot be negative");
              nextPaper.obtained_mark_tmp = raw;
              nextPaper.set_error = "1";
              nextPaper.errorMessage = "Marks cannot be negative";
            } else if (inputMark > maxMarkNum) {
              myToast.notify_warning(
                `Obtained mark cannot be greater than max mark (${max_mark})`
              );
              nextPaper.obtained_mark_tmp = raw;
              nextPaper.set_error = "1";
              nextPaper.errorMessage = `Cannot exceed max mark (${max_mark})`;
            } else {
              nextPaper.obtained_mark_tmp = raw;
              nextPaper.obtained_mark_new = raw;
              nextPaper.changed_mark = "1";
              nextPaper.set_error = "0";
              delete nextPaper.errorMessage;
            }
          }
        }
      }

      if (paperIndex >= 0) {
        list[paperIndex] = nextPaper;
      } else {
        list.push(nextPaper);
      }

      return updated;
    });
  };

  const saveStudentMarks = useCallback(() => {
    if (selectedStudentIndex < 0 || !studentList[selectedStudentIndex]) {
      myToast.notify_warning("Please select a student");
      return;
    }

    const student = studentList[selectedStudentIndex];
    const studentId = student.student_enrollment_no;
    const { classId, sectionId } = classSectionData;

    if (!classId || !sectionId) {
      myToast.notify_warning("Please select a class");
      return;
    }

    const subjects = getFilteredSubjectList(studentId) || [];
    const hasErrors = subjects.some((subject) => {
      const marks = getStudentSubjectMarks(studentId, subject.subject_id);
      return marks.some(
        (mark) => mark.changed_mark === "1" && mark.set_error === "1"
      );
    });
    if (hasErrors) {
      myToast.notify_warning(
        "Fix invalid marks before saving (number or AB, no blank)"
      );
      return;
    }

    const hasBlankChangedMark = subjects.some((subject) => {
      const marks = getStudentSubjectMarks(studentId, subject.subject_id);
      return marks.some(
        (mark) =>
          mark.changed_mark === "1" &&
          mark.is_mark_system === "1" &&
          markInputBlankOrSpaceError(
            mark.obtained_mark_new ?? mark.obtained_mark_tmp ?? ""
          )
      );
    });
    if (hasBlankChangedMark) {
      myToast.notify_warning("Mark cannot be blank or contain spaces");
      return;
    }

    setSavingStudentId(studentId);

    const strSubjects = subjects
      .map((subject) => {
        const subjectId = subject.subject_id;
        const marks = getStudentSubjectMarks(studentId, subjectId);

        const subjectPaper = marks
          .filter((mark) => mark.changed_mark === "1")
          .map((mark) => {
            const isMarkSystem = mark.is_mark_system === "1";

            let obtainedMark = "";
            if (
              mark.obtained_mark_new !== null &&
              mark.obtained_mark_new !== undefined
            ) {
              obtainedMark = String(mark.obtained_mark_new);
            } else if (
              mark.obtained_mark_tmp !== null &&
              mark.obtained_mark_tmp !== undefined
            ) {
              obtainedMark = String(mark.obtained_mark_tmp);
            } else if (
              mark.obtained_mark !== null &&
              mark.obtained_mark !== undefined
            ) {
              obtainedMark = String(mark.obtained_mark);
            }
            if (isAbsentMarkCode(obtainedMark)) {
              obtainedMark = ABSENT_MARK;
            }

            let gradeObtained = "";
            if (
              mark.grade_obtained_new !== null &&
              mark.grade_obtained_new !== undefined
            ) {
              gradeObtained = String(mark.grade_obtained_new);
            } else if (
              mark.grade_obtained_tmp !== null &&
              mark.grade_obtained_tmp !== undefined
            ) {
              gradeObtained = String(mark.grade_obtained_tmp);
            } else if (
              mark.grade_obtained !== null &&
              mark.grade_obtained !== undefined
            ) {
              gradeObtained = String(mark.grade_obtained);
            }

            const markObj = [
              {
                is_mark_system: mark.is_mark_system || "0",
                obtained_mark: obtainedMark,
                max_mark: mark.max_mark || "",
                is_grade_system: mark.is_grade_system || "0",
                max_grade: mark.max_grade || "",
                grade_obtained: gradeObtained,
              },
            ];

            return {
              exam_paper_id: mark.exam_paper_id,
              mark: JSON.stringify(markObj),
            };
          });

        if (subjectPaper.length === 0) {
          return null;
        }

        return {
          subject_id: subjectId,
          subject_paper: JSON.stringify(subjectPaper),
        };
      })
      .filter((subject) => subject !== null);

    const params = new URLSearchParams({
      student_id: studentId,
      session_id: SESSION_ID,
      exam_id: EXAM_ID,
      class_id: classId,
      section_id: sectionId,
      str_subjects: JSON.stringify(strSubjects),
      school_code: SessionManager.getSchoolCode(),
    });

    axios
      .post(`${API_BASE()}AddStudentMarkAllSubjects.php`, params)
      .then((response) => {
        setSavingStudentId(null);
        if (response.data.api_status === "OK") {
          myToast.notify_success(`Marks saved for ${student.stu_name}`);
          setStudentMarks((prev) => {
            const updated = { ...prev };
            const studentIdLower = studentId.toLowerCase();
            if (updated[studentIdLower]) {
              Object.keys(updated[studentIdLower]).forEach((subjectId) => {
                if (updated[studentIdLower][subjectId]) {
                  updated[studentIdLower][subjectId] = updated[
                    studentIdLower
                  ][subjectId].map((mark) => {
                    if (mark.changed_mark === "1") {
                      return {
                        ...mark,
                        obtained_mark:
                          mark.obtained_mark_new || mark.obtained_mark || "",
                        grade_obtained:
                          mark.grade_obtained_new || mark.grade_obtained || "",
                        obtained_mark_tmp:
                          mark.obtained_mark_new || mark.obtained_mark || "",
                        grade_obtained_tmp:
                          mark.grade_obtained_new || mark.grade_obtained || "",
                        changed_mark: "0",
                      };
                    }
                    return mark;
                  });
                }
              });
            }
            return updated;
          });
        } else {
          myToast.notify_warning(
            response.data.api_remark || "Some marks could not be saved"
          );
        }
      })
      .catch((error) => {
        setSavingStudentId(null);
        myToast.notify_error("Error saving marks");
        console.error("Error saving marks:", error);
      });
  }, [
    selectedStudentIndex,
    studentList,
    classSectionData,
    EXAM_ID,
    SESSION_ID,
    getStudentSubjectMarks,
    getFilteredSubjectList,
  ]);

  const filteredSubjectsForSelectedStudent = useMemo(() => {
    if (!selectedStudent || subjectList.length === 0) return [];
    return getFilteredSubjectList(selectedStudent.student_enrollment_no);
  }, [selectedStudent, subjectList, getFilteredSubjectList]);

  const marksProgress = useMemo(() => {
    if (!selectedStudent) {
      return { saved: 0, total: 0, percentage: 0 };
    }
    return getStudentMarksProgress(selectedStudent.student_enrollment_no);
  }, [selectedStudent, getStudentMarksProgress, studentMarks]);

  const selectedStudentSubjectsCount = filteredSubjectsForSelectedStudent.length;

  //Created By Cursor
  const handlePrev = () => {
    if (selectedStudentIndex > 0) {
      setSelectedStudentIndex(selectedStudentIndex - 1);
    }
  };

  //Created By Cursor
  const handleNext = () => {
    if (selectedStudentIndex < studentList.length - 1) {
      setSelectedStudentIndex(selectedStudentIndex + 1);
    }
  };

  //Created By Cursor
  const handleAvatarError = (studentId) => {
    setAvatarFallback((prev) => ({ ...prev, [studentId]: true }));
  };

  if (!loggedIn) return <Redirect to="/Login" />;
  if (bootLoading) return <MyLoader />;

  const detailsPath = `/AdminMain/ExamDetails/${EXAM_ID}/${SESSION_ID}/${encodeURIComponent(
    EXAM_NAME || ""
  )}/${encodeURIComponent(EXAM_STATUS || "")}`;

  const isSavingSelected =
    selectedStudent &&
    savingStudentId === selectedStudent.student_enrollment_no;

  return (
    <div
      className="content payroll-page sm-page eamstw-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}>
                {" "}
                Add Mark Student Wise
              </span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton onClick={() => history.goBack()} />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaUser />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Mark Entry · Student Wise</h1>
              <p className="sm-hero__sub">
                {EXAM_NAME || "Exam"} · Enter marks for all subjects per student
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            {EXAM_STATUS ? (
              <span className="eamstw-status-pill">{EXAM_STATUS}</span>
            ) : null}
            <button
              type="button"
              onClick={() => getExamParticipatingClass({ soft: true })}
              disabled={refreshing}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <Link to={detailsPath} className="eamstw-hero-link">
              Exam Details
            </Link>
            <Link to="/AdminMain/ExamList" className="eamstw-hero-link">
              Exam List
            </Link>
          </div>
        </header>

        <div className="eamstw-content">
          <div className="eamstw-kpi-row">
            <span className="eamstw-kpi">
              <FaGraduationCap aria-hidden />
              {classList.length} classes
            </span>
            <span className="eamstw-kpi">
              <FaUsers aria-hidden />
              {studentList.length} students
            </span>
            <span className="eamstw-kpi">
              <FaBookOpen aria-hidden />
              {selectedStudent
                ? `${selectedStudentSubjectsCount} subjects`
                : "— subjects"}
            </span>
            <span className="eamstw-kpi">
              <FaClipboardList aria-hidden />
              {selectedStudent ? `${marksProgress.percentage}% progress` : "—"}
            </span>
          </div>

          <section className="eamstw-section-card" aria-label="Select class">
            <div className="eamstw-section-card__header">
              <h2 className="eamstw-section-card__title">Select Class</h2>
              <span className="eamstw-section-card__count">
                {classList.length}
              </span>
            </div>
            <div className="eamstw-section-card__body">
              {classList.length === 0 ? (
                <div className="eamstw-empty">
                  <p className="eamstw-empty__title">No participating classes</p>
                  <p className="eamstw-empty__hint">
                    No classes are participating in this exam. Please configure
                    exam settings first.
                  </p>
                </div>
              ) : (
                <div className="eamstw-chip-grid">
                  {classList.map((item, index) => (
                    <button
                      key={`${item.class_id}-${item.section_id}-${index}`}
                      type="button"
                      className={`eamstw-chip${
                        selectedClassPosition === index ? " is-active" : ""
                      }`}
                      onClick={() => setSelectedClassPosition(index)}
                    >
                      {classLabel(item)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          {(studentList.length > 0 || loadingStudents) && selectedClass ? (
            <section className="eamstw-section-card" aria-label="Select student">
              <div className="eamstw-section-card__header">
                <h2 className="eamstw-section-card__title">
                  Select Student · {classLabel(selectedClass)}
                </h2>
                <span className="eamstw-section-card__count">
                  {studentList.length}
                </span>
              </div>
              <div className="eamstw-section-card__body">
                {loadingStudents ? (
                  <div className="eamstw-loading-inline">
                    <MyLoader />
                    <span>Loading students…</span>
                  </div>
                ) : (
                  <div className="eamstw-student-scroll">
                    {studentList.map((student, index) => {
                      const enrollmentNo = student.student_enrollment_no;
                      const isSelected = selectedStudentIndex === index;
                      const isSaving = savingStudentId === enrollmentNo;
                      const hasMarks = hasStudentMarksEntered(enrollmentNo);
                      const studentProgress =
                        getStudentMarksProgress(enrollmentNo);
                      const isCompleted = studentProgress.percentage === 100;
                      const hasPartialMarks = hasMarks && !isCompleted;

                      return (
                        <button
                          key={enrollmentNo || index}
                          type="button"
                          className={`eamstw-student-card${
                            isSelected ? " is-selected" : ""
                          }${isCompleted ? " is-complete" : ""}${
                            hasPartialMarks ? " is-partial" : ""
                          }${isSaving ? " is-saving" : ""}`}
                          onClick={() => setSelectedStudentIndex(index)}
                        >
                          {isCompleted ? (
                            <span className="eamstw-student-card__check" aria-hidden>
                              <FaCheck />
                            </span>
                          ) : null}
                          <span className="eamstw-student-roll">
                            {student.student_roll_no || index + 1}
                          </span>
                          <span
                            className="eamstw-student-name"
                            title={student.stu_name}
                          >
                            {student.stu_name}
                          </span>
                          {studentProgress.total > 0 ? (
                            <span className="eamstw-student-progress">
                              {studentProgress.saved}/{studentProgress.total} (
                              {studentProgress.percentage}%)
                            </span>
                          ) : null}
                          {isSaving ? (
                            <span className="eamstw-student-saving">
                              Saving…
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          ) : null}

          {selectedStudent ? (
            <section
              className="eamstw-section-card"
              aria-label="Selected student"
            >
              <div className="eamstw-section-card__header">
                <h2 className="eamstw-section-card__title">Selected Student</h2>
              </div>
              <div className="eamstw-section-card__body">
                <div className="eamstw-banner">
                  <div className="eamstw-banner__main">
                    <div className="eamstw-banner__avatar">
                      <div className="eamstw-banner__avatar-ring">
                        <div className="eamstw-banner__avatar-inner">
                          {avatarFallback[selectedStudent.student_enrollment_no] ? (
                            <span className="eamstw-banner__initial">
                              {studentInitial(selectedStudent.stu_name)}
                            </span>
                          ) : (
                            <img
                              className="eamstw-banner__img"
                              src={studentPhotoSrc(selectedStudent)}
                              alt={selectedStudent.stu_name || "Student"}
                              onError={() =>
                                handleAvatarError(
                                  selectedStudent.student_enrollment_no
                                )
                              }
                            />
                          )}
                        </div>
                      </div>
                      {marksProgress.percentage === 100 ? (
                        <span className="eamstw-banner__status" aria-hidden>
                          <FaCheck />
                        </span>
                      ) : null}
                    </div>
                    <div className="eamstw-banner__info">
                      <h3 className="eamstw-banner__name">
                        {selectedStudent.stu_name}
                      </h3>
                      <div className="eamstw-banner__meta">
                        <span className="eamstw-banner__meta-item">
                          <FaHashtag aria-hidden />
                          Roll {selectedStudent.student_roll_no}
                        </span>
                        <span className="eamstw-banner__meta-item">
                          <FaGraduationCap aria-hidden />
                          {classLabel(selectedClass)}
                        </span>
                        {marksProgress.total > 0 ? (
                          <span
                            className={`eamstw-banner__meta-item${
                              marksProgress.percentage === 100
                                ? " is-complete"
                                : marksProgress.percentage > 0
                                  ? " is-partial"
                                  : ""
                            }`}
                          >
                            <FaClipboardList aria-hidden />
                            {marksProgress.saved}/{marksProgress.total} marks (
                            {marksProgress.percentage}%)
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="eamstw-banner__nav">
                    <button
                      type="button"
                      className="eamstw-banner__nav-btn"
                      onClick={handlePrev}
                      disabled={selectedStudentIndex <= 0}
                      aria-label="Previous student"
                    >
                      <FaChevronLeft aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="eamstw-banner__nav-btn"
                      onClick={handleNext}
                      disabled={
                        selectedStudentIndex >= studentList.length - 1
                      }
                      aria-label="Next student"
                    >
                      <FaChevronRight aria-hidden />
                    </button>
                  </div>
                </div>
              </div>
            </section>
          ) : studentList.length > 0 && !loadingStudents ? (
            <section className="eamstw-section-card">
              <div className="eamstw-section-card__body">
                <div className="eamstw-empty">
                  <p className="eamstw-empty__title">Select a student</p>
                  <p className="eamstw-empty__hint">
                    Choose a student from the list above to enter marks for all
                    subjects.
                  </p>
                </div>
              </div>
            </section>
          ) : null}

          {selectedStudent && filteredSubjectsForSelectedStudent.length > 0 ? (
            <>
              {loadingMarks ? (
                <div className="eamstw-loading-inline">
                  <MyLoader />
                  <span>Loading marks…</span>
                </div>
              ) : null}

              {filteredSubjectsForSelectedStudent.map((subject, subjectIndex) => {
                const subjectId = subject.subject_id;
                const papers = getSubjectPapers(subjectId);
                const isAlt =
                  subject.subject_type?.toLowerCase() ===
                  TAG_ALT.toLowerCase();

                return (
                  <section
                    key={subjectId || subjectIndex}
                    className="eamstw-section-card"
                    aria-label={`Marks for ${subject.subject_name}`}
                  >
                    <div className="eamstw-section-card__header">
                      <h2 className="eamstw-section-card__title">
                        {subject.subject_name}
                        {isAlt ? " (Alternative)" : ""}
                      </h2>
                      <span className="eamstw-section-card__count">
                        {papers.length}
                      </span>
                    </div>
                    <div className="eamstw-section-card__body">
                      {papers.length > 0 ? (
                        <div className="eamstw-paper-list">
                          {papers.map((paper, paperIndex) => {
                            const paperId = paper.exam_paper_id?.toLowerCase();
                            const isMarkSystem = paper.is_mark_system === "1";
                            const studentIdLower =
                              selectedStudent.student_enrollment_no?.toLowerCase();
                            const subjectIdLower = subjectId?.toLowerCase();
                            const studentMarksData =
                              studentMarks[studentIdLower];
                            let existingMark = null;

                            if (studentMarksData) {
                              const subjectKey = Object.keys(
                                studentMarksData
                              ).find(
                                (key) =>
                                  key.toLowerCase() === subjectIdLower
                              );

                              if (subjectKey && studentMarksData[subjectKey]) {
                                existingMark = studentMarksData[
                                  subjectKey
                                ].find(
                                  (m) =>
                                    m.exam_paper_id?.toLowerCase() === paperId
                                );
                              }
                            }

                            const currentValue = existingMark
                              ? isMarkSystem
                                ? existingMark.obtained_mark_tmp !==
                                    undefined &&
                                  existingMark.obtained_mark_tmp !== null
                                  ? String(existingMark.obtained_mark_tmp)
                                  : existingMark.obtained_mark
                                    ? String(existingMark.obtained_mark)
                                    : ""
                                : existingMark.grade_obtained_tmp !==
                                    undefined &&
                                  existingMark.grade_obtained_tmp !== null
                                  ? String(existingMark.grade_obtained_tmp)
                                  : existingMark.grade_obtained
                                    ? String(existingMark.grade_obtained)
                                    : ""
                              : "";

                            const hasSavedMark =
                              existingMark?.obtained_mark ||
                              existingMark?.grade_obtained;
                            const isDirty = existingMark?.changed_mark === "1";
                            const hasError = existingMark?.set_error === "1";

                            return (
                              <div
                                key={paper.exam_paper_id || paperIndex}
                                className="eamstw-paper-row"
                              >
                                <div className="eamstw-paper-name">
                                  <FaBookOpen aria-hidden />
                                  <span>{paper.exam_paper_name}</span>
                                  {paper.paper_type ? (
                                    <span
                                      className={`eamstw-paper-badge${
                                        isAlt ? " is-alt" : ""
                                      }`}
                                    >
                                      {paper.paper_type}
                                    </span>
                                  ) : null}
                                </div>
                                <span className="eamstw-paper-max">
                                  Max:{" "}
                                  {isMarkSystem
                                    ? paper.max_mark
                                    : paper.max_grade}
                                </span>
                                <div className="eamstw-paper-input-wrap">
                                  <label
                                    className="eamstw-paper-label"
                                    htmlFor={`mark-${subjectId}-${paper.exam_paper_id}`}
                                  >
                                    {isMarkSystem ? "Mark" : "Grade"}
                                  </label>
                                  <input
                                    id={`mark-${subjectId}-${paper.exam_paper_id}`}
                                    type="text"
                                    inputMode="text"
                                    className={`eamstw-input${
                                      hasError
                                        ? " is-error"
                                        : hasSavedMark && !isDirty
                                          ? " is-saved"
                                          : ""
                                    }${
                                      isDirty && !hasError ? " is-dirty" : ""
                                    }`}
                                    value={currentValue || ""}
                                    onChange={(e) =>
                                      handleMarkChange(
                                        selectedStudent.student_enrollment_no,
                                        subjectId,
                                        paper.exam_paper_id,
                                        e.target.value,
                                        isMarkSystem,
                                        paper
                                      )
                                    }
                                    onKeyDown={handleMarkKeyDown}
                                    placeholder={
                                      isMarkSystem
                                        ? "Mark / AB"
                                        : "Enter grade"
                                    }
                                    disabled={loadingMarks}
                                  />
                                  {hasError && existingMark?.errorMessage ? (
                                    <span className="eamstw-input-error">
                                      {existingMark.errorMessage}
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="eamstw-empty">
                          <p className="eamstw-empty__title">
                            No papers for this subject
                          </p>
                        </div>
                      )}
                    </div>
                  </section>
                );
              })}

              <div className="eamstw-save-bar">
                <button
                  type="button"
                  className="eamstw-save-btn"
                  onClick={saveStudentMarks}
                  disabled={loadingMarks || isSavingSelected}
                >
                  <FaSave aria-hidden />
                  {isSavingSelected ? "Saving…" : "Save All Marks"}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ExamAddMarkStudentWise;
